/**
 * A film recorded to a video file, in the browser, at the shape an ad needs.
 *
 * Each frame is drawn at its own time — frame `i` is the film at `i / fps` — into a
 * canvas of the format's size, and handed to the browser's own recorder the moment it
 * is drawn. Nothing is uploaded and nothing is installed: the video is a `Blob` on the
 * device that recorded it, saved through a download link. MP4 where the browser can
 * write it (Chrome, Edge and Safari), WebM where it cannot.
 *
 * The recorder stamps frames with the time they arrive, so the film is played into it in
 * real time, paced by the clock rather than by the screen: a thirty second film takes
 * thirty seconds to record, and the tab has to stay in front while it does. With sound,
 * the clock is the soundtrack's: it is rendered whole first (`renderSoundtrack`), played
 * into the recording, and each frame is drawn the moment the sound reaches it.
 */

import { renderFrame, type Film, type Site } from "./film.js";
import type { Palette } from "./palette.js";
import { TAIL, renderSoundtrack, type SoundOptions } from "./sound.js";

export interface Format {
  id: string;
  label: string;
  width: number;
  height: number;
}

export const FORMATS: readonly Format[] = [
  { id: "16x9", label: "Widescreen 16:9 · 1920 × 1080 (YouTube, the site)", width: 1920, height: 1080 },
  { id: "9x16", label: "Vertical 9:16 · 1080 × 1920 (Reels, TikTok, Shorts)", width: 1080, height: 1920 },
  { id: "1x1", label: "Square 1:1 · 1080 × 1080", width: 1080, height: 1080 },
  { id: "4x5", label: "Feed 4:5 · 1080 × 1350", width: 1080, height: 1350 },
];

/**
 * What to ask the recorder for, best first. H.264 in MP4 is what every ad platform takes,
 * and it is asked for by name: a browser that says yes to a bare `video/mp4` may fill it
 * with VP9, which some platforms refuse. Without H.264, a WebM says what it is.
 */
const TYPES = [
  "video/mp4;codecs=avc1.640028",
  "video/mp4;codecs=avc1.4d0028",
  "video/mp4;codecs=avc1",
  "video/webm;codecs=vp9",
  "video/webm;codecs=vp8",
  "video/webm",
];

/** The same, with a soundtrack: AAC beside H.264 where the browser has it, Opus beside VP9. */
const TYPES_WITH_SOUND = [
  "video/mp4;codecs=avc1.640028,mp4a.40.2",
  "video/mp4;codecs=avc1.4d0028,mp4a.40.2",
  "video/mp4;codecs=avc1,mp4a.40.2",
  "video/mp4;codecs=avc1.640028,opus",
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
];

/** The best video type this browser can record, or `undefined` when it records none. */
export function recordingType(withSound = false): string | undefined {
  if (typeof MediaRecorder === "undefined") {
    return undefined;
  }
  return (withSound ? TYPES_WITH_SOUND : TYPES).find((type) => MediaRecorder.isTypeSupported(type));
}

export interface Recording {
  blob: Blob;
  extension: "mp4" | "webm";
  /**
   * How many frames were drawn more than a frame late: the device fell behind the clock,
   * and the video will stutter there. A recording with any to speak of is worth redoing
   * with the other tabs closed.
   */
  late: number;
  frames: number;
}

/** A canvas of the format's size, in the document (a detached canvas may record nothing) but off screen. */
function stage(format: Format): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement("canvas");
  canvas.width = format.width;
  canvas.height = format.height;
  canvas.className = "film-offscreen";
  canvas.setAttribute("aria-hidden", "true");
  document.body.append(canvas);
  const ctx = canvas.getContext("2d");
  if (ctx === null) {
    canvas.remove();
    throw new Error("This browser would not give the recorder a canvas to draw on.");
  }
  return { canvas, ctx };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function recordFilm(
  film: Film,
  palette: Palette,
  site: Site,
  format: Format,
  fps: number,
  sound: SoundOptions | undefined,
  onProgress: (done: number) => void,
  signal?: AbortSignal,
): Promise<Recording> {
  const withSound = sound !== undefined && film.score !== undefined && (sound.effects || sound.music);
  const type = recordingType(withSound);
  if (type === undefined) {
    throw new Error("This browser cannot record video. Chrome, Edge, Firefox and Safari can.");
  }
  // Made before anything awaits, while the click that asked for it still counts.
  const audio = withSound ? new AudioContext({ sampleRate: 48_000, latencyHint: "interactive" }) : undefined;
  const { canvas, ctx } = stage(format);
  // Stopped on every way out — done, stopped or failed — so no capture outlives its recording.
  let stream: MediaStream | undefined;
  try {
    const soundtrack = withSound ? await renderSoundtrack(film, site, sound) : undefined;
    renderFrame(ctx, film, 0, format.width, format.height, palette, site);
    const manual = typeof CanvasCaptureMediaStreamTrack !== "undefined" && "requestFrame" in CanvasCaptureMediaStreamTrack.prototype;
    stream = canvas.captureStream(manual ? 0 : fps);
    const track = stream.getVideoTracks()[0] as CanvasCaptureMediaStreamTrack | undefined;
    let source: AudioBufferSourceNode | undefined;
    if (audio !== undefined && soundtrack !== undefined) {
      await audio.resume();
      const into = audio.createMediaStreamDestination();
      source = audio.createBufferSource();
      source.buffer = soundtrack;
      source.connect(into);
      for (const each of into.stream.getAudioTracks()) {
        stream.addTrack(each);
      }
    }
    const recorder = new MediaRecorder(stream, {
      mimeType: type,
      videoBitsPerSecond: 16_000_000,
      audioBitsPerSecond: 192_000,
    });
    const chunks: Blob[] = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };
    const stopped = new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
    });
    recorder.start(1000);

    // Frames are paced by the page's clock, which is fine-grained: the sound's moves in
    // steps as large as a frame, and frames drawn to it arrive in bunches and stutter.
    // The sound is started on its own clock and the two are lined up once, through the
    // output timestamp the context gives for exactly that; over thirty seconds the two
    // clocks do not drift apart by anything a viewer could see.
    const now = (): number => performance.now() / 1000;
    let start = now() + 0.15;
    if (audio !== undefined && source !== undefined) {
      const startsAt = audio.currentTime + 0.15;
      source.start(startsAt);
      const stamp = audio.getOutputTimestamp();
      if (stamp.contextTime !== undefined && stamp.performanceTime !== undefined && stamp.performanceTime > 0) {
        // When `startsAt` is rendered — and so reaches the recording — on the page's clock.
        const latency = (audio.outputLatency || 0) + (audio.baseLatency || 0);
        start = stamp.performanceTime / 1000 + (startsAt - stamp.contextTime) - latency;
      } else {
        start = now() + 0.15;
      }
    }
    const until = async (at: number): Promise<void> => {
      while (now() < at) {
        await sleep(Math.max(1, (at - now()) * 1000 - 2));
      }
    };

    const frames = Math.round(film.duration * fps);
    let late = 0;
    await until(start);
    for (let i = 0; i <= frames; i += 1) {
      if (now() - (start + i / fps) > 1 / fps) {
        late += 1;
      }
      if (signal?.aborted === true) {
        recorder.stop();
        await stopped;
        throw new DOMException("The recording was stopped.", "AbortError");
      }
      renderFrame(ctx, film, i / fps, format.width, format.height, palette, site);
      if (manual) {
        track?.requestFrame();
      }
      onProgress(i / frames);
      await until(start + (i + 1) / fps);
    }
    // The end card a moment longer, while the last chord rings: a player that trims the
    // last frame keeps it.
    const tail = Math.round(TAIL * fps);
    for (let i = 1; i <= tail; i += 1) {
      if (manual) {
        track?.requestFrame();
      }
      await until(start + (frames + i) / fps);
    }
    recorder.stop();
    await stopped;
    return {
      blob: new Blob(chunks, { type }),
      extension: type.startsWith("video/mp4") ? "mp4" : "webm",
      late,
      frames: frames + 1,
    };
  } finally {
    stream?.getTracks().forEach((each) => each.stop());
    canvas.remove();
    void audio?.close();
  }
}

/** One frame of the film as a PNG, at the format's size: a thumbnail, a poster. */
export async function stillFrame(film: Film, palette: Palette, site: Site, format: Format, t: number): Promise<Blob> {
  const { canvas, ctx } = stage(format);
  try {
    renderFrame(ctx, film, t, format.width, format.height, palette, site);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => (blob === null ? reject(new Error("The frame could not be saved.")) : resolve(blob)), "image/png");
    });
  } finally {
    canvas.remove();
  }
}

/** What a saved file is called: the brand, the film, the shape. */
export function fileName(site: Site, film: Film, format: Format, extension: string): string {
  const brand = site.brand.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return `${brand}-${film.slug}-film-${format.id}.${extension}`;
}
